/**
 * Read/write a layer's media pointer (`meta.mediaId`). In a DESIGN it holds an
 * entry id into `designs.media`; in a TEMPLATE it holds a media id (D-47) —
 * this module is id-space agnostic, the caller decides which it writes.
 *
 * Readers are WIDENED (`meta: unknown` in) per the narrowed-meta rule: layer
 * meta types are augmented, and `next build` rejects assumptions `tsc` allows.
 * Use updaters as `prev ? withMediaId(prev, id) : prev`.
 */

export function readMediaId(meta: unknown): string | undefined {
  const mediaId = (meta as { mediaId?: unknown } | null | undefined)?.mediaId;
  return typeof mediaId === "string" && mediaId.length > 0
    ? mediaId
    : undefined;
}

/**
 * The temporary id of an upload still in flight on this layer. While it is
 * set, the layer DISPLAYS the import — its blob is seeded under this id — even
 * though its real pointer still names the image it is replacing: that pointer
 * stays until the upload lands, so an autosave mid-upload keeps the old image.
 *
 * **It lives on its own key, never on `meta.asset.id`.** That field is the
 * LEGACY pointer, and on a pre-cutover layer it is the only image reference
 * the layer has. Writing the transient id there overwrote it, so beginning an
 * import destroyed the existing image — and because an `assets.id` has no
 * `media.id` counterpart, that loss is unrecoverable, unlike the in-flight
 * upload it was trading against.
 *
 * The legacy shape is still READ, for documents saved mid-upload before the
 * split. It is only ever an import id when `importStatus` says so — otherwise
 * `asset.id` is a real pointer and must be left alone.
 */
export function readImportingMediaId(meta: unknown): string | undefined {
  const asset = (meta as { asset?: unknown } | null | undefined)?.asset as
    | { id?: unknown; importStatus?: unknown }
    | null
    | undefined;
  // A recorded failure is authoritative: a marker beside it is a leftover,
  // not an upload in flight.
  if (asset?.importStatus === "failed") return undefined;

  const own = (meta as { importingMediaId?: unknown } | null | undefined)
    ?.importingMediaId;
  if (typeof own === "string" && own.length > 0) return own;

  return asset?.importStatus === "uploading" &&
    typeof asset.id === "string" &&
    asset.id
    ? asset.id
    : undefined;
}

/**
 * Mark an import as in flight without touching any pointer the layer already
 * has — the whole point of the separate key.
 */
export function withImportingMediaId<M extends object>(
  meta: M,
  importingMediaId: string,
) {
  return { ...meta, importingMediaId };
}

/**
 * Clear the in-flight marker, leaving every pointer untouched. Prefer
 * {@link withImportCompleted} / {@link withImportFailed}, which only clear the
 * marker when it is still that import's. A stale marker left by a session that
 * ended mid-upload is NOT retired on load: it is inert (`readLiveImportId`
 * ignores an id this session never seeded) and is cleared by the layer's next
 * import.
 */
export function withoutImportingMediaId<M extends object>(meta: M) {
  const { importingMediaId: _dropped, ...rest } = meta as M & {
    importingMediaId?: unknown;
  };
  const asset = (rest as { asset?: unknown }).asset;
  if (!asset || typeof asset !== "object") return rest;
  // A pre-split document carries the marker inside `meta.asset`; drop only the
  // status, so a legacy `asset.id` beneath it survives as the real pointer.
  const { importStatus: _status, ...keep } = asset as Record<string, unknown>;
  return { ...rest, asset: keep };
}

/**
 * Import `importId` landed: point the layer at its entry and retire the import
 * bookkeeping — the marker and any recorded status. A recorded failure is
 * cleared too: it may belong to a LATER import that failed first, and the
 * layer now shows a good image. Only a later import still in flight keeps its
 * marker, so it goes on displaying until it lands and writes its own pointer.
 */
export function withImportCompleted<M extends object>(
  meta: M,
  importId: string,
  pointer: string,
) {
  const placed = withMediaId(meta, pointer);
  const live = readImportingMediaId(meta);
  return live === undefined || live === importId
    ? withoutImportingMediaId(placed)
    : placed;
}

/**
 * Import `importId` failed: retire its marker and flag the failure. Retiring
 * the marker is what lets the layer fall back to the pointer it still has (a
 * failed replace shows the saved image) and the failed badge show — the
 * import's blob stays seeded, so a marker left behind would read as an upload
 * still in flight for the rest of the session. A later import's marker is left
 * alone.
 */
export function withImportFailed<M extends object>(meta: M, importId: string) {
  if (readImportingMediaId(meta) !== importId) return meta;
  const cleared = withoutImportingMediaId(meta);
  const asset = (cleared as { asset?: unknown }).asset;
  return {
    ...cleared,
    asset: {
      ...(asset && typeof asset === "object" ? asset : {}),
      importStatus: "failed" as const,
    },
  };
}

/**
 * Point the layer at `mediaId` and drop the legacy asset pointer
 * (`meta.asset.id`) and its persisted signed URL (`meta.asset.preview`).
 * Display-only fields on `meta.asset` (name, dimensions) survive.
 */
export function withMediaId<M extends object>(meta: M, mediaId: string) {
  return { ...stripLegacyAssetPointer(meta), mediaId };
}

/**
 * Take a layer's image off it entirely: the pointer, the persisted `value`,
 * and — for an image FILL — the `url(...)` in `--background-image` and the
 * `image` fill type, which would otherwise persist a presigned URL that
 * nothing re-resolves (the save strip only clears URLs behind a pointer).
 */
export function withoutLayerImage<
  L extends { value?: unknown; meta?: unknown; cssVars?: unknown },
>(layer: L): L {
  const cssVars =
    (layer.cssVars as Record<string, unknown> | null | undefined) ?? undefined;
  const bg = cssVars?.["--background-image"];
  const imageFill =
    cssVars?.["--fill-type"] === "image" ||
    (typeof bg === "string" && /^\s*url\(/.test(bg));
  return {
    ...layer,
    value: typeof layer.value === "string" ? "" : layer.value,
    meta: withoutMediaId((layer.meta ?? {}) as object),
    ...(cssVars && imageFill
      ? {
          cssVars: {
            ...cssVars,
            "--fill-type": "none",
            "--background-image": "",
          },
        }
      : {}),
  };
}

/** Remove the media pointer (and any legacy asset pointer). */
export function withoutMediaId<M extends object>(meta: M) {
  const { mediaId: _dropped, ...rest } = stripLegacyAssetPointer(meta) as M & {
    mediaId?: unknown;
  };
  return rest;
}

function stripLegacyAssetPointer<M extends object>(meta: M): M {
  const asset = (meta as { asset?: unknown }).asset;
  if (!asset || typeof asset !== "object") return meta;
  const {
    id: _id,
    preview: _preview,
    ...display
  } = asset as Record<string, unknown>;
  return { ...meta, asset: display };
}
